import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { planAddToCart, readPartialAdd, readQuantityLimits } from '@theme/quantity-limits';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  /** @type {ResizeObserver | undefined} */
  #iconFitObserver;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);

    if (this.hasAttribute('data-hide-icon-on-wrap')) {
      this.#iconFitObserver = new ResizeObserver(() => this.fitIcon());
      this.#iconFitObserver.observe(this.refs.addToCartButton);
      // The label is wider once the web font swaps in, which doesn't resize the button.
      document.fonts?.ready.then(() => this.fitIcon());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
    this.#iconFitObserver?.disconnect();
  }

  /**
   * Hides the icon when the label can't sit on one line beside it (long French labels in a
   * narrow row), so the label gets the icon's room instead of wrapping. It lays the button out
   * with the icon shown and lets the browser say whether the label wrapped or overflowed, so it
   * holds whether the button is sized by its row (PDP) or by its content (sticky bar), and the
   * result doesn't depend on the current state, so it can't flip back and forth.
   */
  fitIcon() {
    if (!this.hasAttribute('data-hide-icon-on-wrap')) return;

    const button = this.refs.addToCartButton;
    const label = button.querySelector('.add-to-cart-text .add-to-cart-text__content');
    if (!(label instanceof HTMLElement) || button.clientWidth === 0) return;

    this.classList.remove('icon-hidden');

    const labelRange = document.createRange();
    labelRange.selectNodeContents(label);
    const laidOut = label.getBoundingClientRect();
    const textWidth = labelRange.getBoundingClientRect().width;

    label.style.whiteSpace = 'nowrap';
    const oneLineHeight = label.getBoundingClientRect().height;
    label.style.whiteSpace = '';

    // Wrapped: taller than one line. Overflowed: a nowrap label (sticky bar on mobile) whose
    // text is wider than the box it got.
    const wrapped = laidOut.height > oneLineHeight + 0.5;
    const overflowed = textWidth > laidOut.width + 0.5;
    this.classList.toggle('icon-hidden', wrapped || overflowed);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (!cartIcon || !addToCartButton) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    // Two flavours. By default the product image flies to the cart. Buttons that opt in with
    // data-fly-to-cart="icon" (the product-card CTA in blocks/nova-buy-buttons.liquid) fly a clone
    // of their own cart icon instead — product cards already show the image right above the button,
    // so a second copy of it reads as noise there.
    if (this.dataset.flyToCart === 'icon') {
      const buttonIcon = addToCartButton.querySelector('.add-to-cart-icon');

      if (!buttonIcon) return;

      flyToCartElement.classList.add('fly-to-cart--icon');
      flyToCartElement.appendChild(buttonIcon.cloneNode(true));
    } else {
      const image = this.dataset.productVariantMedia;

      if (!image) return;

      flyToCartElement.style.setProperty('background-image', `url(${image})`);
    }

    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /**
   * The quantity limit message shown by the last add to cart, kept on screen until the
   * quantity or the variant changes.
   * @type {string}
   */
  #limitMessage = '';

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#hideLimitMessage, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#hideLimitMessage();

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    this.#checkQuantityLimits(formData).then(({ canAdd, inCart }) => {
      if (canAdd) {
        this.#addToCart(form, formData, event, inCart);
      } else {
        // Nothing was added: drop the "added" state the button click already started.
        this.refs.addToCartButtonContainer?.refs.addToCartButton?.classList.remove('atc-added');
      }
    });
  }

  /**
   * Applies the variant's quantity limits before the add (ticket 1888, see
   * assets/quantity-limits.js). Too many: only what is left gets added, the quantity selector shows
   * it and the message says how many were added. Nothing left, or not enough for the minimum:
   * nothing is added and the message says why.
   *
   * The limits come from the quantity selector's input (PDP, quick add) or, on a form without
   * one (product card, cross-sell), from the form itself, which then adds what the minimum
   * still needs instead of 1.
   *
   * @param {FormData} formData - The add to cart form data; its quantity is set in place.
   * @returns {Promise<{ canAdd: boolean, inCart: number | null }>} Whether anything can be
   *   added, and how many units the cart held (null when the variant has no limits to check).
   */
  async #checkQuantityLimits(formData) {
    const quantityInput = this.querySelector('quantity-selector-component input[name="quantity"]');
    const source = quantityInput instanceof HTMLInputElement ? quantityInput : this;
    const requested = Number(formData.get('quantity')) || null;

    const plan = await planAddToCart(Number(formData.get('id')), requested, readQuantityLimits(source));

    if (plan.quantity === 0) {
      this.#showLimitMessage(plan.message);
      return { canAdd: false, inCart: plan.inCart };
    }

    formData.set('quantity', String(plan.quantity));
    if (requested !== null && plan.quantity !== requested) this.#setSelectorQuantity(plan.quantity);
    if (plan.message) this.#showLimitMessage(plan.message);

    return { canAdd: true, inCart: plan.inCart };
  }

  /**
   * Shows the quantity really added in the quantity selector.
   * @param {number} quantity
   */
  #setSelectorQuantity(quantity) {
    const selector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const quantityInput = selector?.querySelector('input[name="quantity"]');
    if (!(quantityInput instanceof HTMLInputElement)) return;

    quantityInput.value = String(quantity);
    selector.updateButtonStates?.();
  }

  /**
   * Shopify added only part of the quantity (a 422): shows what really went in, in the selector
   * and the message, and sets the cart bubble to the real count.
   *
   * @param {number} variantId
   * @param {number} inCart - How many units the cart held before the add.
   */
  async #reportPartialAdd(variantId, inCart) {
    const { added, itemCount, message } = await readPartialAdd(variantId, inCart);

    if (added > 0) {
      this.#setSelectorQuantity(added);
    } else {
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.classList.remove('atc-added');
    }
    this.#showLimitMessage(message);

    if (itemCount === null) return;

    // An absolute count (not from the product form, which would add the requested quantity).
    this.dispatchEvent(
      new CartUpdateEvent({}, this.id, {
        didError: added === 0,
        source: 'product-form-quantity-limit',
        itemCount,
        productId: this.dataset.productId,
      })
    );
  }

  /**
   * @param {string} text
   */
  #showLimitMessage(text) {
    this.#limitMessage = text;
    this.#showMessage(text);
  }

  #hideLimitMessage = () => {
    if (!this.#limitMessage) return;

    this.#limitMessage = '';
    this.refs.addToCartTextError?.classList.add('hidden');
    this.#clearLiveRegionText();
  };

  /**
   * Shows a message under the add to cart button and announces it.
   * @param {string} text
   */
  #showMessage(text) {
    const { addToCartTextError } = this.refs;

    this.#setLiveRegionText(text);
    if (!addToCartTextError) return;

    addToCartTextError.classList.remove('hidden');

    // Reuse the text node if the user is spam-clicking
    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = text;
    } else {
      addToCartTextError.appendChild(document.createTextNode(text));
    }
  }

  /**
   * Posts the form to the cart.
   * @param {HTMLFormElement} form
   * @param {FormData} formData
   * @param {Event} event - The submit event.
   * @param {number | null} inCart - How many units the cart held, when the limits were checked.
   */
  #addToCart(form, formData, event, inCart) {
    const { addToCartTextError } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (inCart !== null) {
            this.#reportPartialAdd(Number(formData.get('id')), inCart);
            return;
          }

          if (!addToCartTextError) return;
          this.#limitMessage = '';
          this.#showMessage(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          // A partial add message explains what was just added, so it stays.
          if (addToCartTextError && !this.#limitMessage) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(this.#limitMessage ? `${addedText} ${this.#limitMessage}` : addedText);

            setTimeout(() => {
              if (!this.#limitMessage) this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    this.#hideLimitMessage();
    this.#updateQuantityLimits(event.detail.data.html);

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // The server can render the button disabled for an available variant too
    // (custom.en_magasin_seulement), so the rendered state wins. If the update carries no
    // button, the product didn't change, so the current in-store-only state still holds.
    const renderedDisabled = newAddToCartButton
      ? newAddToCartButton.hasAttribute('disabled')
      : currentAddToCartButton.classList.contains('nova-add-to-cart-button--in-store-only');

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false || renderedDisabled) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
      // The label may have changed length (e.g. "Non disponible") without the button resizing.
      addToCartButtonContainer.fitIcon();
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Gives the quantity selector the limits the server rendered for the new variant.
   * @param {Document | Element | undefined} html - The page or section rendered for the new variant.
   */
  #updateQuantityLimits(html) {
    const selector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (!selector?.updateLimits || !html) return;

    const inputSelector = 'quantity-selector-component input[name="quantity"]';
    const newInput =
      html.querySelector(`product-form-component[data-section-id="${this.dataset.sectionId}"] ${inputSelector}`) ||
      html.querySelector(`product-form-component ${inputSelector}`);

    if (newInput instanceof HTMLInputElement) selector.updateLimits(newInput);
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   *
   * Other forms in the same section (the PDP cross-sell) hear the event too, but #onVariantUpdate
   * only re-enables the form whose product changed, so they skip another product's picker.
   * @param {Event} event
   */
  #onVariantSelected = (event) => {
    const pickerProductId =
      event.target instanceof Element ? event.target.closest('[data-product-id]')?.getAttribute('data-product-id') : null;
    if (pickerProductId && this.dataset.productId && pickerProductId !== this.dataset.productId) return;

    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
